import type { PropertyAgreement } from '../types/listingRequestTypes'

// Amending an agreement inserts a NEW row pointing back at the one it replaces
// and marks the old one `superseded`; both keep the same reference number. The
// agreements endpoint returns every row unfiltered, so without grouping the
// seller's panel would grow a stack of near-identical documents — the current
// one indistinguishable from the ones it replaced.
export interface AgreementWithHistory {
  current: PropertyAgreement
  // Newest replaced version first, so the list reads backwards in time.
  history: PropertyAgreement[]
}

// The current version is the one nothing else replaces. Derived rather than read
// off `status`, because a superseded row is only stamped `superseded` once the
// replacement EXECUTES — between generation and execution both rows are live,
// and the newer one is already the one that matters.
export function groupAgreementsByVersion(agreements: PropertyAgreement[]): AgreementWithHistory[] {
  const replacedIds = new Set(
    agreements
      .map((agreement) => agreement.supersedesAgreementId)
      .filter((id): id is string => id !== null),
  )

  return agreements
    .filter((agreement) => !replacedIds.has(agreement.id))
    .map((current) => ({ current, history: walkSupersededChain(current, agreements) }))
}

function walkSupersededChain(
  current: PropertyAgreement,
  agreements: PropertyAgreement[],
): PropertyAgreement[] {
  const byId = new Map(agreements.map((agreement) => [agreement.id, agreement]))
  const history: PropertyAgreement[] = []
  const visited = new Set<string>([current.id])

  let previousId = current.supersedesAgreementId
  // `visited` guards a chain that points back into itself — bad data would
  // otherwise hang the render thread rather than showing a short history.
  while (previousId !== null && !visited.has(previousId)) {
    const previous = byId.get(previousId)
    if (!previous) break
    history.push(previous)
    visited.add(previous.id)
    previousId = previous.supersedesAgreementId
  }

  return history
}
